/**
 * @linkhr/hr-domain/payroll-config-schemas — Per-resource Zod schemas for
 * payroll configuration types.
 *
 * Background (fe-059): the OpenAPI generator collapsed multiple DTOs into
 * generic `View` and `CreateRequest` schema names. The mitigation is
 * per-resource Zod schemas with `.passthrough()` + minimum-required-field
 * validation, named with the resource prefix to avoid collisions.
 *
 * All schemas use `.passthrough()` to tolerate undocumented fields added by
 * backend (e.g. labels added in future CC-NN entries).
 *
 * Mirrors `components["schemas"]["*View"]` from the generated OpenAPI schema.
 * Field names verified against `packages/api-client/src/generated/schema.d.ts`.
 */

import { z } from "zod";

// ---------------------------------------------------------------------------
// Salary Component — mirrors SalaryComponentView
// ---------------------------------------------------------------------------

export const SalaryComponentView = z
  .object({
    id: z.string().optional(),
    templateId: z.string().optional(),
    componentType: z.string().optional(), // "EARNING" | "DEDUCTION" | "STATUTORY" — kept as string for forward-compat
    code: z.string().optional(),
    nameEn: z.string().optional(),
    nameAr: z.string().optional(),
    amount: z.number().optional(),
    currency: z.string().optional(),
    taxable: z.boolean().optional(),
    sscSubject: z.boolean().optional(),
    // Legacy MSW shape compatibility
    isActive: z.boolean().optional(),
    status: z.enum(["ACTIVE", "ARCHIVED"]).optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  })
  .passthrough();

export type SalaryComponentView = z.infer<typeof SalaryComponentView>;

// ---------------------------------------------------------------------------
// Salary Package Template — mirrors SalaryPackageTemplateView
// ---------------------------------------------------------------------------

export const SalaryPackageTemplateView = z
  .object({
    id: z.string().optional(),
    code: z.string().optional(),
    nameEn: z.string().optional(),
    nameAr: z.string().optional(),
    isActive: z.boolean().optional(),
    status: z.enum(["ACTIVE", "ARCHIVED"]).optional(),
    components: z.array(SalaryComponentView).optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  })
  .passthrough();

export type SalaryPackageTemplateView = z.infer<typeof SalaryPackageTemplateView>;

// ---------------------------------------------------------------------------
// Tax Bracket — mirrors TaxBracketView
// ---------------------------------------------------------------------------

export const TaxBracketView = z
  .object({
    id: z.string().optional(),
    fromAmount: z.number().min(0).optional(),
    toAmount: z.number().nullable().optional(),
    rate: z.number().min(0).max(1).optional(),
    personalExemptionAnnual: z.number().optional(),
    familyExemptionAnnual: z.number().optional(),
    // Optional like every sibling schema here. A `.default("JOD")` at a parse
    // boundary is indistinguishable downstream from a real Jordanian row.
    currency: z.string().optional(),
    effectiveFrom: z.string().optional(),
    effectiveTo: z.string().nullable().optional(),
    tenantVerified: z.boolean().default(false),
    createdAt: z.string().optional(),
  })
  .passthrough();

export type TaxBracketView = z.infer<typeof TaxBracketView>;

// ---------------------------------------------------------------------------
// Contribution Rate — mirrors ContributionRateView
// Note: OpenAPI uses `insurableCeiling` (not `ceiling`) — see schema.d.ts:3494
// ---------------------------------------------------------------------------

export const ContributionRateView = z
  .object({
    id: z.string().optional(),
    employeeRate: z.number().min(0).max(1).optional(),
    employerRate: z.number().min(0).max(1).optional(),
    insurableCeiling: z.number().optional(),
    currency: z.string().optional(),
    effectiveFrom: z.string().optional(),
    effectiveTo: z.string().nullable().optional(),
    tenantVerified: z.boolean().default(false),
    createdAt: z.string().optional(),
  })
  .passthrough();

export type ContributionRateView = z.infer<typeof ContributionRateView>;

// ---------------------------------------------------------------------------
// Service Termination Schedule — mirrors ServiceTerminationScheduleView
// (EOS schedule rows — append-only per D-14)
// ---------------------------------------------------------------------------

export const ServiceTerminationScheduleView = z
  .object({
    id: z.string().optional(),
    contractTypeId: z.string().optional(),
    monthsPerYear: z.number().optional(),
    effectiveFrom: z.string().optional(),
    effectiveTo: z.string().nullable().optional(),
    tenantVerified: z.boolean().default(false),
    createdAt: z.string().optional(),
  })
  .passthrough();

export type ServiceTerminationScheduleView = z.infer<typeof ServiceTerminationScheduleView>;
